import { VwPluralPipe } from '../../pipes/vw-plural.pipe';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  effect,
  inject,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { SubscriptionApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/subscription/subscription-api';
import { LifecycleApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/lifecycle/lifecycle-api';
import { TrialSummaryResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/lifecycle/trial-summary.response';
import { BaseToastComponent } from '../../ui-components/toast-popup/base-toast-component';
import { SharedModule } from '../../ui-components/shared.module';
import { CheckoutPlan, CheckoutService } from '../../@services/front/checkout.service';
import { CheckoutProviderService } from '../../@services/front/checkout-provider.service';
import { PremiumService, TrialOffer } from '../../@services/front/premium.service';
import { PricingService } from '../../@services/front/pricing.service';
import { PosthogService } from '../../@services/front/posthog.service';
import { ErrorStateComponent } from '../../ui-components/error-state/error-state.component';
import { storeUrlFor } from '../../constants/store-urls.constants';
import { PaywallBenefit, benefitsFor, titleKeyFor } from './paywall-copy.util';
import { currentAppLang, formatVwDate } from '../../pipes/vw-date.pipe';

/**
 * Le paywall, unique pour toutes les sources (CR-01, maquette 04).
 *
 * - Titre et premier avantage selon la `source` ; les autres avantages dans
 *   l'ordre de la valeur (`paywall-copy.util.ts`).
 * - L'essai n'est promis que s'il est vrai ici et maintenant
 *   (`PremiumService.trialOffer`) : l'essai maison dans le navigateur, celui
 *   du magasin dans l'app (1.5.1+).
 * - Le prix exact et « Annulable à tout moment » sont sous le bouton, visibles
 *   AVANT tout clic : c'est un argument, pas une mention reléguée en bas.
 */
@Component({
  selector: 'vw-premium-toast',
  imports: [VwPluralPipe, SharedModule, CommonModule, TranslatePipe, ErrorStateComponent],
  templateUrl: './premium-toast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: SubscriptionApi, useClass: SubscriptionApi }],
})
export class PremiumToastComponent
  extends BaseToastComponent
  implements OnChanges
{
  /** Emplacement d'où le paywall a été ouvert (mesure du funnel). */
  @Input() source = 'unknown';

  selectedPlan: CheckoutPlan = 'YEARLY';
  loading = false;
  /** Activation en cours après retour du paiement. */
  activating = false;
  activationSlow = false;
  error = false;

  /**
   * Achat en attente d'un accord extérieur : « Demander à acheter », contrôle
   * parental, moyen de paiement différé. L'accès n'est pas immédiat, et le taire
   * laisserait croire à un échec.
   */
  purchasePending = false;

  /**
   * Bilan de l'essai pour la source `trial_ending` (rappels J-5 et J-1).
   * `null` : pas encore chargé, ou illisible (le bilan est alors masqué).
   */
  trialSummary: TrialSummaryResponse | null = null;

  /** Résultat de la dernière restauration : `null` = aucune tentative. */
  restored: boolean | null = null;
  restoring = false;

  private readonly authApi = new AuthApi();
  private readonly lifecycleApi = new LifecycleApi();
  private readonly checkout = inject(CheckoutProviderService);
  private readonly stripeCheckout = inject(CheckoutService);
  private readonly posthog = inject(PosthogService);
  private readonly translate = inject(TranslateService);
  readonly premiumService = inject(PremiumService);
  /** Les montants affichés viennent du fournisseur, jamais des libellés. */
  readonly pricing = inject(PricingService);

  /** L'ouverture en cours est préparée (mesure, prix, bilan) ; remis à zéro à la fermeture. */
  private openingPrepared = false;

  constructor(private readonly subscriptionApi: SubscriptionApi) {
    super();
    // Feuille ouverte AVANT que l'API ait dit si le premium est ouvert : le rappel de fin
    // d'essai ouvre `/home?paywall=trial_ending` au démarrage, quand `subscriptions/me` n'a
    // pas encore répondu. L'ouverture se préparait alors sans rien charger, et les prix
    // restaient en barres grises (revue design P1.7.2). Elle se prépare dès que l'API répond.
    effect(() => {
      if (this.premiumService.premiumEnabled() && untracked(() => this.isOpen)) untracked(() => this.prepareOpening());
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen']) return;
    if (changes['isOpen'].currentValue) this.prepareOpening();
    else this.openingPrepared = false;
  }

  /** Une fois par ouverture, et seulement premium ouvert : mesure, prix, abonnement, bilan d'essai. */
  private prepareOpening(): void {
    if (this.openingPrepared || !this.premiumService.premiumEnabled()) return;
    this.openingPrepared = true;
    this.error = false;
    this.posthog.capture('paywall_viewed', { source: this.source });
    this.openedAt = Date.now();
    void this.pricing.load();
    void this.premiumService.refresh().then(() => this.preselectPlan());
    this.preselectPlan();
    if (this.source === 'trial_ending') void this.loadTrialSummary();
  }

  /**
   * Feuille « Ton essai se termine », ouverte par les rappels J-5 et J-1.
   * Retombe sur le paywall ordinaire si l'essai est déjà fini (lien ouvert
   * trop tard) : il n'y a plus rien à « garder ».
   */
  get isTrialEnding(): boolean {
    return (
      this.source === 'trial_ending' &&
      !(this.trialSummary && !this.trialSummary.trialEndsAt)
    );
  }

  /** « samedi 28 novembre » dans la langue de l'app, ou vide tant que le bilan n'est pas là. */
  get trialEndLabel(): string {
    return formatVwDate(this.trialSummary?.trialEndsAt, 'long', currentAppLang(this.translate));
  }

  /** Date du premier débit ; vide = débit immédiat. */
  get billingStartLabel(): string {
    return formatVwDate(this.trialSummary?.billingStartsAt, 'numeric', currentAppLang(this.translate));
  }

  private async loadTrialSummary(): Promise<void> {
    this.trialSummary = null;
    try {
      this.trialSummary = await this.lifecycleApi.getTrialSummary();
    } catch {
      // Bilan masqué, formules seules : la feuille reste utilisable.
    }
  }

  /** Ouverture en cours, pour la durée passée sur le paywall (`paywall_closed`). */
  private openedAt = 0;

  override onClose(data?: any): void {
    this.posthog.capture('paywall_closed', {
      source: this.source,
      duration_s: this.openedAt ? Math.round((Date.now() - this.openedAt) / 1000) : null,
      plan: this.selectedPlan,
      trial_offered: this.canStartTrial,
      result: data?.subscribed ? 'subscribed' : data?.started ? 'trial_started' : data?.kept ? 'kept' : 'dismissed',
    });
    super.onClose(data);
  }

  /** « Je reste en gratuit » : on referme, sans insister. */
  stayFree(): void {
    this.onClose({ stayedFree: true });
  }

  /**
   * Ouvre sur la formule DÉJÀ souscrite quand il y en a une.
   *
   * Présenter l'annuel coché à quelqu'un qui paie au mois donne l'impression
   * qu'on lui propose son abonnement actuel — puis le débite différemment.
   */
  private preselectPlan(): void {
    const current = this.premiumService.subscription()?.plan;
    if (current === 'MONTHLY' || current === 'YEARLY') {
      this.selectedPlan = current;
    }
  }

  /** L'utilisateur a déjà cette formule : rien à changer. */
  get isCurrentPlan(): boolean {
    return this.premiumService.subscription()?.plan === this.selectedPlan;
  }

  /**
   * Fin d'essai sur la formule déjà choisie (revue design P1.7.2) : le bouton
   * principal dit « Garder l'annuel » et reste actif. Avant, il s'éteignait en
   * « C'est ta formule actuelle » et seul « Je reste en gratuit » répondait.
   *
   * Rien n'est à acheter : le premier débit tombe déjà à la fin de l'essai. Si
   * la personne avait annulé le renouvellement, garder = réactiver, ce qui ne
   * se fait que chez nous (Stripe) ; dans le magasin, la feuille reste comme avant.
   */
  get canKeepCurrentPlan(): boolean {
    if (!this.isTrialEnding || !this.isCurrentPlan) return false;
    return !this.premiumService.subscription()?.cancelAtPeriodEnd || !this.isStore;
  }

  /** La réactivation demandée par « Garder l'annuel » a échoué. */
  keepFailed = false;

  /** « Garder l'annuel » : réactive si besoin, puis referme la feuille. */
  async keepCurrentPlan(): Promise<void> {
    if (!this.canKeepCurrentPlan || this.loading) return;
    this.keepFailed = false;
    if (this.premiumService.subscription()?.cancelAtPeriodEnd) {
      this.loading = true;
      try {
        await this.subscriptionApi.reactivate();
        await this.premiumService.refresh();
      } catch {
        this.keepFailed = true;
        return;
      } finally {
        this.loading = false;
      }
    }
    this.onClose({ kept: true });
  }

  /** Bouton principal : garder la formule en fin d'essai, sinon l'achat ou l'essai. */
  onPrimaryClick(): Promise<void> {
    return this.canKeepCurrentPlan ? this.keepCurrentPlan() : this.onPrimary();
  }

  /**
   * Change-t-il de formule ? Il doit savoir à partir de quand.
   */
  get isPlanChange(): boolean {
    const current = this.premiumService.subscription()?.plan;
    return Boolean(current) && current !== this.selectedPlan;
  }

  /** Échéance de la période déjà réglée, formatée. */
  get currentPeriodEndLabel(): string {
    return formatVwDate(this.premiumService.subscription()?.currentPeriodEnd, 'numeric', currentAppLang(this.translate));
  }

  /** Titre de la feuille, selon d'où elle a été ouverte. */
  get titleKey(): string {
    return titleKeyFor(this.source);
  }

  /** Les quatre avantages, celui de la source en tête. */
  get benefits(): PaywallBenefit[] {
    return benefitsFor(this.source);
  }

  /** L'essai offert sur la formule choisie, ou null (`PremiumService.trialOffer`). */
  get trial(): TrialOffer | null {
    return this.premiumService.trialOffer(this.selectedPlan);
  }

  /**
   * Essai proposé seulement s'il peut être lancé ici (`PremiumService.trialOffer`)
   * ET payé ensuite ici : dans un navigateur sans paiement Stripe, un essai
   * finirait sans moyen de payer (CR-02).
   */
  get canStartTrial(): boolean {
    return this.trial !== null && !this.webCheckoutUnavailable;
  }

  /**
   * Bouton principal : l'essai maison se crée chez nous, sans carte ; l'essai
   * du magasin, lui, démarre par l'achat de la formule (le magasin ne prélève
   * qu'à son terme).
   */
  onPrimary(): Promise<void> {
    const trial = this.trial;
    return trial && !trial.store ? this.startTrial() : this.subscribe();
  }

  /**
   * Ancienne version de l'app, sans achat intégré : on n'y vend rien (ni
   * Stripe, ni essai maison), on invite à la mettre à jour.
   */
  get isOutdatedApp(): boolean {
    return this.checkout.isOutdatedApp;
  }

  /**
   * Navigateur sans paiement Stripe configuré (aucun prix servi) : l'achat se
   * fait dans l'app des magasins. Se rallume seul quand Stripe répond.
   */
  get webCheckoutUnavailable(): boolean {
    return (
      !this.isStore &&
      !this.isOutdatedApp &&
      this.pricing.loaded() &&
      !this.pricing.loading() &&
      !this.pricing.failed() &&
      this.pricing.prices().length === 0
    );
  }

  /** Les prix ne sont pas encore arrivés (attente bornée par PricingService). */
  get pricesLoading(): boolean {
    return this.pricing.loading() || !this.pricing.loaded();
  }

  /**
   * Aucun prix à montrer : panne, délai dépassé, ou magasin qui ne répond rien.
   * On le dit et on propose de réessayer, au lieu de « —/an » sans fin.
   */
  get pricesUnavailable(): boolean {
    if (this.isOutdatedApp || this.pricesLoading) return false;
    return this.pricing.failed() || (this.isStore && this.pricing.prices().length === 0);
  }

  /** Pas de prix pour la formule choisie, pas d'achat : on ne vend pas à l'aveugle. */
  get canPurchase(): boolean {
    return this.pricing.hasPrice(this.selectedPlan) && !this.isCurrentPlan;
  }

  retryPrices(): void {
    void this.pricing.load();
  }

  /**
   * Ancien binaire : la fiche du magasin, ouverte hors de la WebView (canal
   * `openExternalUrl`, sinon `openOAuthUrl` que connaît l'ancien binaire, sinon
   * un onglet), comme les liens légaux.
   */
  openStore(): void {
    this.stripeCheckout.openOutside(storeUrlFor(navigator.userAgent));
  }

  /**
   * Conditions d'utilisation et confidentialité : Apple exige ces deux liens sur
   * l'écran d'achat d'un abonnement (règle 3.1.2).
   */
  readonly termsUrl = 'https://vegiwise.com/policies/terms-of-service';
  readonly privacyUrl = 'https://vegiwise.com/policies/privacy-policy';

  openLegal(url: string): void {
    this.stripeCheckout.openOutside(url);
  }

  /** Un invité n'a pas de compte auquel rattacher un abonnement. */
  get isGuest(): boolean {
    return this.authApi.getGroups().includes('GUEST');
  }

  /**
   * L'achat passe-t-il par le magasin ?
   *
   * Commande l'affichage de « Restaurer mes achats », qu'Apple EXIGE d'une app
   * vendant un abonnement : sans lui, quelqu'un qui réinstalle l'app ne retrouve
   * pas ce qu'il a payé, et c'est un motif de refus.
   */
  get isStore(): boolean {
    return this.checkout.isStore;
  }

  selectPlan(plan: CheckoutPlan): void {
    this.selectedPlan = plan;
  }

  async startTrial(): Promise<void> {
    // L'essai du magasin ne passe jamais par notre essai maison (règle 3.1.1).
    if (!this.trial || this.trial.store) return;
    this.loading = true;
    this.error = false;
    try {
      await this.subscriptionApi.startTrial();
      this.posthog.capture('trial_started', { source: this.source });
      // Le droit vient d'être créé : sans rafraîchir le jeton, l'app ne le
      // verrait qu'à sa prochaine expiration.
      await this.premiumService.refreshEntitlement();
      this.onClose({ started: true });
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }

  async subscribe(): Promise<void> {
    if (!this.canPurchase) return;
    this.loading = true;
    this.error = false;
    this.purchasePending = false;
    this.activationSlow = false;
    try {
      const outcome = await this.checkout.purchase(this.selectedPlan);
      this.trackPurchaseOutcome(outcome);

      // Refermer la feuille d'achat est un geste ordinaire, pas un incident :
      // aucun message, l'écran reste tel quel.
      if (outcome === 'cancelled') return;

      if (outcome === 'pending') {
        this.purchasePending = true;
        return;
      }

      // Que le paiement se soit conclu dans le magasin (`completed`) ou hors de
      // l'app (`opened`), le DROIT vient de notre webhook — jamais du SDK du
      // prestataire. On attend donc notre propre base.
      this.activating = true;
      const activated = await this.premiumService.awaitActivation();
      this.activationSlow = !activated;
      if (activated) this.onClose({ subscribed: true });
    } catch {
      this.trackPurchaseOutcome('failed');
      this.error = true;
    } finally {
      this.loading = false;
      this.keepSlowActivationVisible();
    }
  }

  /**
   * Activation lente : le message « elle se fera automatiquement » ne s'affiche
   * que tant que `activating` est vrai. Le remettre à faux ici le masquait et
   * ramenait le bouton d'achat devant quelqu'un qui vient de payer.
   */
  private keepSlowActivationVisible(): void {
    if (!this.activationSlow) this.activating = false;
  }

  /** Issue de l'achat (`opened` = paiement Stripe ouvert hors de l'app). */
  private trackPurchaseOutcome(outcome: string): void {
    this.posthog.capture('purchase_outcome', {
      source: this.source,
      plan: this.selectedPlan,
      channel: this.isStore ? 'store' : 'web',
      outcome,
    });
  }

  /** Restauration des achats du magasin. */
  async restorePurchases(): Promise<void> {
    this.restoring = true;
    this.error = false;
    this.restored = null;
    this.activationSlow = false;
    try {
      const found = await this.checkout.restore();
      this.restored = found;
      if (found) {
        // Le magasin a retrouvé un achat ; notre webhook doit encore ouvrir le
        // droit. On attend, comme après un achat.
        this.activating = true;
        const activated = await this.premiumService.awaitActivation();
        this.activationSlow = !activated;
        if (activated) this.onClose({ subscribed: true });
      }
    } catch {
      this.error = true;
    } finally {
      this.restoring = false;
      this.keepSlowActivationVisible();
    }
  }
}
